import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import {
  Search,
  Loader2,
  Target,
  HelpCircle,
  Eye,
  FileText,
  MessageSquare,
  Rocket,
  TrendingUp,
  BarChart3,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

interface Competitor {
  id: string;
  competitor_domain: string;
  competitor_name: string;
  discovery_method: string;
  relevance_score: number;
  created_at: string;
}

interface Question {
  id: string;
  question: string;
  question_type: string;
  word_count: number;
  source: string;
  created_at: string;
}

interface AnswerTracking {
  id: string;
  query: string;
  ai_engine: string;
  brand_mentioned: boolean;
  mention_position?: number;
  mention_count: number;
  tested_at: string;
}

interface LandingPageOpportunity {
  id: string;
  primary_question: string;
  follow_up_questions: string[];
  target_keyword: string;
  priority_score: number;
  status: string;
  page_url?: string;
}

interface MentionOpportunity {
  id: string;
  platform: string;
  opportunity_url: string;
  opportunity_type: string;
  opportunity_text: string;
  relevance_score: number;
  status: string;
}

export default function AEOResearch() {
  const { selectedSite } = useSite();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Read initial values from URL params (e.g., from Product Success Story)
  const initialKeywords = searchParams.get('keywords') || '';
  const initialTab = searchParams.get('tab') || 'competitors';

  const [activeTab, setActiveTab] = useState(initialTab);

  // Per-action loading states
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [landingPagesLoading, setLandingPagesLoading] = useState(false);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  // Step 0: Competitor Discovery
  const [sector, setSector] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [product, setProduct] = useState('');
  const [geography, setGeography] = useState('');
  const [keywords, setKeywords] = useState(initialKeywords);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);

  // Step 1: Questions
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);

  // Step 2: Answer Tracking
  const [answerTracking, setAnswerTracking] = useState<AnswerTracking[]>([]);

  // Step 3: Landing Page Opportunities
  const [landingPages, setLandingPages] = useState<LandingPageOpportunity[]>([]);

  // Step 4: Mention Opportunities
  const [mentions, setMentions] = useState<MentionOpportunity[]>([]);

  // Page Content Generation Dialog
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<{
    title: string;
    metaDescription: string;
    introduction: string;
    sections: Array<{ heading: string; content: string }>;
    conclusion: string;
    cta: string;
  } | null>(null);

  // Handle data from Question Wheel
  useEffect(() => {
    const state = location.state as any;
    console.log('[AEO Research] Location state:', state);

    if (state?.question) {
      console.log('[AEO Research] Processing question from Question Wheel:', state.question);

      // Extract keywords from the question
      const questionKeywords = state.question
        .replace(/\?/g, '')
        .split(' ')
        .filter((word: string) => word.length > 3 && !['what', 'how', 'when', 'where', 'why', 'which', 'does', 'best', 'good'].includes(word.toLowerCase()))
        .slice(0, 3)
        .join(', ');

      console.log('[AEO Research] Extracted keywords:', questionKeywords);
      setKeywords(questionKeywords);

      toast({
        title: '✨ Question Loaded from Question Wheel',
        description: `Keywords extracted: "${questionKeywords}". Click "Discover Competitors" to begin.`,
        duration: 6000,
      });

      // Clear location state after a brief delay to ensure state is set
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location.state]);

  // Discover Competitors (ENHANCED - sends sector/product/geography)
  const handleDiscoverCompetitors = async () => {
    if (!selectedSite?.id) {
      toast({
        title: 'Error',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    const keywordsList = keywords.split(',').map(k => k.trim()).filter(Boolean);
    if (keywordsList.length === 0) {
      toast({
        title: 'Error',
        description: 'Please enter at least one keyword',
        variant: 'destructive',
      });
      return;
    }

    try {
      setDiscoveryLoading(true);
      const response = await authFetch('/api/research/aeo/competitors/discover', {
        method: 'POST',
        body: JSON.stringify({
          site_id: selectedSite.id,
          keywords: keywordsList,
          sector: sector || undefined,
          product_category: productCategory || undefined,
          product: product || undefined,
          geography: geography || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to discover competitors');
      }

      const data = await response.json();
      setCompetitors(data.competitors || []);
      toast({
        title: 'Success',
        description: `Discovered ${data.count} competitors`,
      });
    } catch (error) {
      console.error('Error discovering competitors:', error);
      toast({
        title: 'Error',
        description: 'Failed to discover competitors',
        variant: 'destructive',
      });
    } finally {
      setDiscoveryLoading(false);
    }
  };

  // Load existing competitors
  const loadCompetitors = async () => {
    if (!selectedSite?.id) return;

    try {
      setDiscoveryLoading(true);
      const response = await authFetch(
        `/api/research/aeo/competitors?site_id=${selectedSite.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to load competitors');
      }

      const data = await response.json();
      setCompetitors(data.competitors || []);
    } catch (error) {
      console.error('Error loading competitors:', error);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  // Extract Questions
  const handleExtractQuestions = async () => {
    if (!selectedCompetitor) {
      toast({
        title: 'Error',
        description: 'Please select a competitor first',
        variant: 'destructive',
      });
      return;
    }

    try {
      setQuestionsLoading(true);

      // Pass the original keywords so backend can find relevant questions
      const keywordsArray = keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);

      const response = await authFetch(
        `/api/research/aeo/competitors/${selectedCompetitor}/questions`,
        {
          method: 'POST',
          body: JSON.stringify({
            research_keywords: keywordsArray, // Pass the research context!
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to extract questions');
      }

      const data = await response.json();
      setQuestions(data.questions || []);
      setSelectedQuestions([]); // Clear selected questions when loading new ones
      setActiveTab('questions');
      toast({
        title: 'Success',
        description: `Extracted ${data.count} questions (${data.long_tail_count} long-tail, ${data.high_volume_count || 0} high-volume)`,
      });
    } catch (error) {
      console.error('Error extracting questions:', error);
      toast({
        title: 'Error',
        description: 'Failed to extract questions',
        variant: 'destructive',
      });
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Load Questions for Competitor
  const loadQuestions = async (competitorId: string) => {
    try {
      setQuestionsLoading(true);
      const response = await authFetch(
        `/api/research/aeo/competitors/${competitorId}/questions`
      );

      if (!response.ok) {
        throw new Error('Failed to load questions');
      }

      const data = await response.json();
      setQuestions(data.questions || []);
      setSelectedQuestions([]); // Clear selected questions when switching competitors
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Track Answers for Selected Questions
  const handleTrackAnswers = async () => {
    if (!selectedSite?.id || selectedQuestions.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select questions to track',
        variant: 'destructive',
      });
      return;
    }

    try {
      setTrackingLoading(true);
      const brandName = selectedSite.name || selectedSite.domain;

      for (const questionId of selectedQuestions) {
        const question = questions.find(q => q.id === questionId);
        if (!question) continue;

        await authFetch('/api/research/aeo/tracking/answer', {
          method: 'POST',
          body: JSON.stringify({
            site_id: selectedSite.id,
            query: question.question,
            brand_name: brandName,
          }),
        });
      }

      toast({
        title: 'Success',
        description: `Started tracking ${selectedQuestions.length} questions`,
      });
      setActiveTab('tracking');
      loadAnswerTracking();
    } catch (error) {
      console.error('Error tracking answers:', error);
      toast({
        title: 'Error',
        description: 'Failed to track answers',
        variant: 'destructive',
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  // Load Answer Tracking
  const loadAnswerTracking = async () => {
    if (!selectedSite?.id) return;

    try {
      setTrackingLoading(true);
      const response = await authFetch(
        `/api/research/aeo/tracking?site_id=${selectedSite.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to load tracking');
      }

      const data = await response.json();
      setAnswerTracking(data.tracking || []);
    } catch (error) {
      console.error('Error loading tracking:', error);
    } finally {
      setTrackingLoading(false);
    }
  };

  // Generate Landing Page Opportunities
  const handleGenerateLandingPages = async () => {
    if (!selectedSite?.id || selectedQuestions.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select questions first',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLandingPagesLoading(true);
      const response = await authFetch('/api/research/aeo/opportunities/landing-pages', {
        method: 'POST',
        body: JSON.stringify({
          site_id: selectedSite.id,
          question_ids: selectedQuestions,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate opportunities');
      }

      const data = await response.json();
      setLandingPages(data.opportunities || []);
      setActiveTab('landing-pages');
      toast({
        title: 'Success',
        description: `Generated ${data.count} landing page opportunities with AI briefs`,
      });
    } catch (error) {
      console.error('Error generating landing pages:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate landing page opportunities',
        variant: 'destructive',
      });
    } finally {
      setLandingPagesLoading(false);
    }
  };

  // Generate Full Page Content
  const handleCreatePage = async (opportunityId: string) => {
    try {
      setGeneratingContent(true);
      setShowContentDialog(true);

      const response = await authFetch(
        `/api/research/aeo/opportunities/landing-pages/${opportunityId}/generate-content`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate page content');
      }

      const data = await response.json();
      setGeneratedContent(data.content);
      toast({
        title: 'Success',
        description: 'Landing page content generated successfully',
      });
    } catch (error) {
      console.error('Error generating page content:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate page content',
        variant: 'destructive',
      });
      setShowContentDialog(false);
    } finally {
      setGeneratingContent(false);
    }
  };

  // Copy content to clipboard
  const handleCopyContent = () => {
    if (!generatedContent) return;

    const fullContent = `# ${generatedContent.title}

**Meta Description:** ${generatedContent.metaDescription}

## Introduction

${generatedContent.introduction}

${generatedContent.sections.map(s => `## ${s.heading}\n\n${s.content}`).join('\n\n')}

## Conclusion

${generatedContent.conclusion}

---

**${generatedContent.cta}**`;

    navigator.clipboard.writeText(fullContent);
    toast({
      title: 'Copied!',
      description: 'Content copied to clipboard',
    });
  };

  // Find Mention Opportunities
  const handleFindMentions = async () => {
    if (!selectedSite?.id) {
      toast({
        title: 'Error',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    const keywordsList = keywords.split(',').map(k => k.trim()).filter(Boolean);
    if (keywordsList.length === 0) {
      toast({
        title: 'Error',
        description: 'Please enter keywords',
        variant: 'destructive',
      });
      return;
    }

    try {
      setMentionsLoading(true);
      const response = await authFetch('/api/research/aeo/opportunities/mentions', {
        method: 'POST',
        body: JSON.stringify({
          site_id: selectedSite.id,
          keywords: keywordsList,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to find mentions');
      }

      const data = await response.json();
      setMentions(data.opportunities || []);
      setActiveTab('mentions');
      toast({
        title: 'Success',
        description: `Found ${data.count} mention opportunities (${data.reddit_count || 0} Reddit, ${data.youtube_count || 0} YouTube)`,
      });
    } catch (error) {
      console.error('Error finding mentions:', error);
      toast({
        title: 'Error',
        description: 'Failed to find mention opportunities',
        variant: 'destructive',
      });
    } finally {
      setMentionsLoading(false);
    }
  };

  // Load data when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadCompetitors();
      loadAnswerTracking();
    }
  }, [selectedSite]);

  // Load questions when competitor is selected
  useEffect(() => {
    if (selectedCompetitor) {
      loadQuestions(selectedCompetitor);
    }
  }, [selectedCompetitor]);

  const getQuestionTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      'how-to': 'bg-blue-100 text-blue-800',
      'what-is': 'bg-green-100 text-green-800',
      'comparison': 'bg-purple-100 text-purple-800',
      'feature': 'bg-orange-100 text-orange-800',
    };
    return <Badge className={colors[type] || ''}>{type}</Badge>;
  };

  const getPriorityColor = (score: number) => {
    if (score >= 0.7) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold">No Site Selected</h2>
          <p className="text-muted-foreground">Please select a site to start AEO research</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AEO Research</h1>
          <p className="text-muted-foreground">
            Answer Engine Optimization - Get mentioned in ChatGPT, Perplexity, and Claude
          </p>
        </div>
      </div>

      {/* Question Wheel Integration Banner */}
      {location.state?.question && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 border-blue-200 dark:border-blue-800">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  Researching Question from Question Wheel
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  "{location.state.question}"
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Keywords extracted and ready. Click "Discover Competitors" below to begin analysis.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Indicator */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Research Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className={competitors.length > 0 ? 'text-green-600' : ''}>
                {competitors.length > 0 ? '✓' : '○'} Competitors Discovered
              </span>
              <span className={questions.length > 0 ? 'text-green-600' : ''}>
                {questions.length > 0 ? '✓' : '○'} Questions Extracted
              </span>
              <span className={answerTracking.length > 0 ? 'text-green-600' : ''}>
                {answerTracking.length > 0 ? '✓' : '○'} Answers Tracked
              </span>
              <span className={landingPages.length > 0 ? 'text-green-600' : ''}>
                {landingPages.length > 0 ? '✓' : '○'} Landing Pages
              </span>
              <span className={mentions.length > 0 ? 'text-green-600' : ''}>
                {mentions.length > 0 ? '✓' : '○'} Mentions Found
              </span>
            </div>
            <Progress
              value={
                ((competitors.length > 0 ? 20 : 0) +
                  (questions.length > 0 ? 20 : 0) +
                  (answerTracking.length > 0 ? 20 : 0) +
                  (landingPages.length > 0 ? 20 : 0) +
                  (mentions.length > 0 ? 20 : 0))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="competitors">
            <Target className="h-4 w-4 mr-2" />
            Competitors
          </TabsTrigger>
          <TabsTrigger value="questions">
            <HelpCircle className="h-4 w-4 mr-2" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="tracking">
            <Eye className="h-4 w-4 mr-2" />
            AI Tracking
          </TabsTrigger>
          <TabsTrigger value="landing-pages">
            <FileText className="h-4 w-4 mr-2" />
            Landing Pages
          </TabsTrigger>
          <TabsTrigger value="mentions">
            <MessageSquare className="h-4 w-4 mr-2" />
            Mentions
          </TabsTrigger>
        </TabsList>

        {/* Step 0: Competitor Discovery */}
        <TabsContent value="competitors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Discover Competitors</CardTitle>
              <CardDescription>
                <strong>What this does:</strong> Identifies the top websites ranking for your target keywords.
                These competitors are getting traffic from search engines and AI engines (ChatGPT, Perplexity, Claude)
                for topics relevant to your business.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Input
                    placeholder="e.g., E-commerce, SaaS, Healthcare"
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Product Category</Label>
                  <Input
                    placeholder="e.g., Project Management, CRM"
                    value={productCategory}
                    onChange={(e) => setProductCategory(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Product</Label>
                  <Input
                    placeholder="e.g., Task Tracking Software"
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geography</Label>
                  <Input
                    placeholder="e.g., North America, Europe, Global"
                    value={geography}
                    onChange={(e) => setGeography(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Keywords (comma-separated)</Label>
                <Input
                  placeholder="e.g., project management software, task tracking, team collaboration"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </div>
              <Button onClick={handleDiscoverCompetitors} disabled={discoveryLoading}>
                {discoveryLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Search className="mr-2 h-4 w-4" />
                Discover Competitors
              </Button>
            </CardContent>
          </Card>

          {/* Competitors Table */}
          {competitors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Discovered Competitors ({competitors.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Discovery Method</TableHead>
                      <TableHead>Relevance Score</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {competitors.map((comp) => (
                      <TableRow
                        key={comp.id}
                        className={selectedCompetitor === comp.id ? 'bg-blue-50' : ''}
                      >
                        <TableCell className="font-medium">{comp.competitor_domain}</TableCell>
                        <TableCell>{comp.competitor_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{comp.discovery_method}</Badge>
                        </TableCell>
                        <TableCell>
                          <Progress value={comp.relevance_score * 100} className="w-20" />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={selectedCompetitor === comp.id ? 'default' : 'outline'}
                            onClick={() => {
                              setSelectedCompetitor(comp.id);
                              setActiveTab('questions');
                            }}
                          >
                            Analyze
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Step 1: Questions */}
        <TabsContent value="questions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Competitor Questions</CardTitle>
              <CardDescription>
                <strong>What this shows:</strong> The top questions that bring traffic to your competitors.
                These are real questions people ask on Google that lead them to competitor websites.
                {selectedCompetitor && (
                  <div className="mt-2 text-sm">
                    Currently viewing: {competitors.find(c => c.id === selectedCompetitor)?.competitor_domain || 'selected competitor'}
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCompetitor && (
                <Button onClick={handleExtractQuestions} disabled={questionsLoading}>
                  {questionsLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Extract Questions
                </Button>
              )}
            </CardContent>
          </Card>

          {questions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Extracted Questions ({questions.length})</CardTitle>
                <CardDescription>
                  {questions.filter(q => q.word_count >= 25).length} long-tail questions (25+ words)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button onClick={handleTrackAnswers} disabled={selectedQuestions.length === 0 || trackingLoading}>
                      {trackingLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Track {selectedQuestions.length} Selected
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleGenerateLandingPages}
                      disabled={selectedQuestions.length === 0 || landingPagesLoading}
                    >
                      {landingPagesLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Generate Landing Pages
                    </Button>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <input
                            type="checkbox"
                            checked={selectedQuestions.length === questions.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedQuestions(questions.map(q => q.id));
                              } else {
                                setSelectedQuestions([]);
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Question</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Words</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {questions.map((q) => (
                        <TableRow key={q.id}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedQuestions.includes(q.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedQuestions([...selectedQuestions, q.id]);
                                } else {
                                  setSelectedQuestions(selectedQuestions.filter(id => id !== q.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{q.question}</TableCell>
                          <TableCell>{getQuestionTypeBadge(q.question_type)}</TableCell>
                          <TableCell>
                            <Badge variant={q.word_count >= 25 ? 'default' : 'outline'}>
                              {q.word_count}
                            </Badge>
                          </TableCell>
                          <TableCell>{q.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Step 2: Answer Tracking */}
        <TabsContent value="tracking" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Engine Answer Tracking</CardTitle>
              <CardDescription>
                <strong>What this reveals:</strong> Is your brand mentioned when AI engines (ChatGPT, Perplexity, Claude, Gemini)
                answer these questions? This shows whether you're part of the AI-powered search results or being left out.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {answerTracking.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No tracking data yet. Select questions to track.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Query</TableHead>
                      <TableHead>AI Engine</TableHead>
                      <TableHead>Brand Mentioned</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Mention Count</TableHead>
                      <TableHead>Answer Preview</TableHead>
                      <TableHead>Tested At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {answerTracking.map((track) => (
                      <TableRow key={track.id}>
                        <TableCell className="font-medium max-w-xs truncate">{track.query}</TableCell>
                        <TableCell>
                          <Badge>{track.ai_engine}</Badge>
                        </TableCell>
                        <TableCell>
                          {track.brand_mentioned ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>{track.mention_position || 'N/A'}</TableCell>
                        <TableCell>{track.mention_count}</TableCell>
                        <TableCell className="max-w-md">
                          <div className="text-sm text-muted-foreground truncate">
                            {track.answer_snapshot ?
                              track.answer_snapshot.substring(0, 100) + '...' :
                              'No answer captured'
                            }
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{new Date(track.tested_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Step 3: Landing Page Opportunities */}
        <TabsContent value="landing-pages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Landing Page Opportunities</CardTitle>
              <CardDescription>
                <strong>What you can create:</strong> Which pages could you create to answer these questions and gain relevance?
                These are content opportunities where you can compete for traffic and AI citations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {landingPages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No opportunities yet. Generate from selected questions.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Primary Question</TableHead>
                      <TableHead>Target Keyword</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {landingPages.map((lp) => (
                      <TableRow key={lp.id}>
                        <TableCell className="font-medium">{lp.primary_question}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{lp.target_keyword}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={lp.priority_score * 100} className="w-20" />
                            <span className={`text-sm ${getPriorityColor(lp.priority_score)}`}>
                              {(lp.priority_score * 100).toFixed(0)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge>{lp.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {lp.page_url ? (
                            <Button size="sm" variant="outline" asChild>
                              <a href={lp.page_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCreatePage(lp.id)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Create Page
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Step 4: Mention Opportunities */}
        <TabsContent value="mentions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Offsite Mention Opportunities</CardTitle>
              <CardDescription>
                <strong>Where to get citations:</strong> Where could you get mentioned answering these questions on Reddit, YouTube,
                forums, and other platforms? These are places where you can build authority and get cited by AI engines.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleFindMentions} disabled={mentionsLoading}>
                {mentionsLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Rocket className="mr-2 h-4 w-4" />
                Find Mention Opportunities
              </Button>

              {mentions.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Opportunity</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Relevance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mentions.map((mention) => (
                      <TableRow key={mention.id}>
                        <TableCell>
                          <Badge>{mention.platform}</Badge>
                        </TableCell>
                        <TableCell className="max-w-md truncate">
                          {mention.opportunity_text}
                        </TableCell>
                        <TableCell>{mention.opportunity_type}</TableCell>
                        <TableCell>
                          <Progress value={mention.relevance_score * 100} className="w-20" />
                        </TableCell>
                        <TableCell>
                          <Badge variant={mention.status === 'open' ? 'default' : 'outline'}>
                            {mention.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" asChild>
                            <a
                              href={mention.opportunity_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Page Content Generation Dialog */}
      <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generated Landing Page Content</DialogTitle>
            <DialogDescription>
              Review and copy the generated content below
            </DialogDescription>
          </DialogHeader>

          {generatingContent ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Generating content with AI...</span>
            </div>
          ) : generatedContent ? (
            <div className="space-y-6">
              {/* Title */}
              <div>
                <Label className="text-sm font-semibold">Title (H1)</Label>
                <Input
                  value={generatedContent.title}
                  readOnly
                  className="mt-1 font-semibold text-lg"
                />
              </div>

              {/* Meta Description */}
              <div>
                <Label className="text-sm font-semibold">Meta Description</Label>
                <Textarea
                  value={generatedContent.metaDescription}
                  readOnly
                  rows={2}
                  className="mt-1"
                />
              </div>

              {/* Introduction */}
              <div>
                <Label className="text-sm font-semibold">Introduction</Label>
                <Textarea
                  value={generatedContent.introduction}
                  readOnly
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Sections */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold">Main Sections</Label>
                {generatedContent.sections.map((section, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-2">
                    <Input
                      value={section.heading}
                      readOnly
                      className="font-semibold"
                      placeholder={`Section ${index + 1} Heading`}
                    />
                    <Textarea
                      value={section.content}
                      readOnly
                      rows={4}
                      className="mt-2"
                    />
                  </div>
                ))}
              </div>

              {/* Conclusion */}
              <div>
                <Label className="text-sm font-semibold">Conclusion</Label>
                <Textarea
                  value={generatedContent.conclusion}
                  readOnly
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* CTA */}
              <div>
                <Label className="text-sm font-semibold">Call-to-Action</Label>
                <Input
                  value={generatedContent.cta}
                  readOnly
                  className="mt-1 font-medium"
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContentDialog(false)}>
              Close
            </Button>
            {generatedContent && (
              <Button onClick={handleCopyContent}>
                <FileText className="h-4 w-4 mr-2" />
                Copy All Content
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
